import {  useState } from "react";
import { useAuth } from "../../hooks/auth";
import { useCanAccess } from "../../hooks/useCanAccess";
import { useRoles } from "../../hooks/useRoles";
import { useChat } from "../../hooks/chat/useChat";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chatService } from "../../services/ChatService";
import { MessageList } from "./shared/MessageList";
import { TicketForm } from "./shared/TicketForm";

import {
  ChatIcon,
  MinimizeIcon,
  MaximizeIcon,
  CloseIcon,
  AttachmentIcon,
} from "./shared/ChatIcons";
import { useRef, useEffect } from "react";
import { PaperAirplaneIcon, XCircleIcon, XMarkIcon, ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { socketService } from "@/api/socket";

interface Props {
  isWidget?:boolean;
}
export default function ChatBox({ isWidget = false }: Props) {
  const { user } = useAuth();
  const { roles } = useRoles();
  const canAccess = useCanAccess();
  const canUseChatbox = isWidget || canAccess({ permission: "use-chatbox" }) ;
  const userId =
    user?.user_id ||
    localStorage.getItem("cacuserid") ||
    sessionStorage.getItem("cacuserid");
  const canCreateTicket = canAccess({ permission: "create-ticket" });
  const [messageReceived] = useState(false);

  const userRole = roles[0] ?? '';

  const {
    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    newMessage,
    setNewMessage,
    replyTo,
    setReplyTo,
    chatId,
    setChatId,
    selectedDepartment,
    setSelectedDepartment,
    isSending,
    departments,
    isUploading,
    hasOnlineStaff,
    ticketForm,
    setTicketForm,
    handleFileUpload,
    handleSendMessage,
    handleChatEnd,
    attachmentPreview,
    setAttachmentPreview,
    handleSendAttachment,
    departmentAvailability,
    departmentRoles,
  } = useChat(false);

  const { data: chatData } = useQuery(
    ["chat-history", chatId, messageReceived],
    () => (chatId ? chatService.getChatHistory(chatId) : null),
    {
        enabled: !!chatId,
    }
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (chatId) {
        const socket = socketService.socketInstance;
        
        socket?.on("chat.message", () => {
            queryClient.invalidateQueries(["chat-history", chatId]);
        });
        
        socket?.on("chat-status", () => {
            queryClient.invalidateQueries(["chat-history", chatId]);
        });

        return () => {
            socket?.off("chat.message");
            socket?.off("chat-status");
        };
    }
  }, [chatId, queryClient]);

  useEffect(() => {
    if (!userId || chatId) return;
    const savedChatId = localStorage.getItem(`activeChatId-${userId}`);
    if (!savedChatId) return;

    setChatId(savedChatId);
    setIsOpen(true);
    chatService.subscribeToChat(savedChatId, userId, userRole || "User");
  }, [userId]);

  useEffect(() => {
    if (!chatId && userId) {
      localStorage.removeItem(`activeChatId-${userId}`);
    }
  }, [chatId, userId]);

  const startNewChatFlow = () => {
    setChatId(null);
    setSelectedDepartment("");
    if (userId) localStorage.removeItem(`activeChatId-${userId}`);
  };

  useEffect(() => {
    if (departments?.length > 0 && !ticketForm.departments) {
      setTicketForm((prev) => ({
        ...prev,
        departments: departments[0],
      }));
    }
  }, [departments]);

  const startNewChat = async () => {
    if (!userId || !selectedDepartment) return;

    try {
      const response = await chatService.startUserChat(
        { staff_role: selectedDepartment },
        userId
      );

      setChatId(response.chat_id);
      localStorage.setItem(`activeChatId-${userId}`, response.chat_id);
      setIsOpen(true);
      setIsMinimized(false);

      chatService.subscribeToChat(response.chat_id, userId, userRole || "User");
    } catch (error) {
      console.error("Failed to start chat:", error);
    }
  };

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Department Roles:", departmentRoles);
    console.log("Selected Department:", selectedDepartment);
    console.log("Role ID:", departmentRoles[selectedDepartment]);

    const departmentRoleId = Number(departmentRoles[selectedDepartment]);

    const ticketData = {
      subject: ticketForm.subject,
      description: ticketForm.description,
      department_role_id: departmentRoleId,
      priority: ticketForm.priority,
      user_id: userId,
    };

    try {
      await chatService.createTicketFromChat(ticketData);
      setTicketForm({
        subject: "",
        description: "",
        departments: departments[0] || "",
        priority: "medium",
      });
      setSelectedDepartment("");
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create ticket:", error);
    }
  };

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (chatId && !isSending) {
      inputRef.current?.focus();
    }
  }, [chatId, isSending, newMessage]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [newMessage]);

  if (!canUseChatbox) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-primary-500 text-white rounded-full p-4 shadow-lg hover:bg-blue-600"
        >
          <ChatIcon className="w-6 h-6" />
        </button>
      ) : (
        <div className="bg-white rounded-lg shadow-xl w-96">
          <div className="flex flex-col">
            <div className="p-3 border-b bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg flex justify-between items-center">
              <h3 className="font-semibold text-lg">Chat Support</h3>
              <div className="flex items-center gap-0.5">
                {chatId && chatData?.chat_room?.status !== "ended" && (
                  <button
                    onClick={handleChatEnd}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                    title={chatData?.chat_room?.status === "pending" ? "Cancel Request" : "End Chat"}
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    {chatData?.chat_room?.status === "pending" ? "Cancel Request" : "End Chat"}
                  </button>
                )}
                {chatId && chatData?.chat_room?.status === "ended" && (
                  <button
                    onClick={startNewChatFlow}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                    title="New Chat"
                  >
                    <ChatIcon className="w-4 h-4" />
                    New Chat
                  </button>
                )}
                <span className="w-px h-4 bg-white/30 mx-1" />
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                  title={isMinimized ? "Maximize" : "Minimize"}
                >
                  {isMinimized ? (
                    <MaximizeIcon className="w-4 h-4" />
                  ) : (
                    <MinimizeIcon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                  title="Close"
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <div className="h-[500px] flex flex-col">
                {!chatId ? (
                  <div className="p-4">
                    {hasOnlineStaff ? (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Department
                          </label>
                          <select
                            value={selectedDepartment}
                            onChange={(e) =>
                              setSelectedDepartment(e.target.value)
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="">Select Department</option>
                            {departments?.map((dept: string) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </select>
                        </div>
                        {!selectedDepartment ? (
                          <p className="text-sm text-gray-600 text-center">
                            Please select a department to continue
                          </p>
                        ) : departmentAvailability[selectedDepartment] ? (
                          <button
                            onClick={startNewChat}
                            disabled={!selectedDepartment || isSending}
                            className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
                          >
                            Start Chat
                          </button>
                        ) : (
                          <TicketForm
                            ticketForm={ticketForm}
                            setTicketForm={setTicketForm}
                            onSubmit={handleTicketSubmit}
                          />
                        )}
                      </>
                    ) : canCreateTicket ? (
                      <TicketForm
                        ticketForm={ticketForm}
                        setTicketForm={setTicketForm}
                        departments={departments}
                        selectedDepartment={selectedDepartment}
                        onDepartmentChange={setSelectedDepartment}
                        showDepartmentSelect
                        onSubmit={handleTicketSubmit}
                      />
                    ) : (
                      <p className="text-center text-gray-600">
                        You don't have permission to create tickets
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <MessageList
                      messages={chatData?.messages}
                      userId={userId}
                      isGuest={false}
                      chatRoom={chatData?.chat_room}
                      chatStatus={chatData?.chat_room?.status}
                      onReply={setReplyTo}
                    />
                    {replyTo && (
                      <div className="px-4 pt-2 flex items-center justify-between bg-gray-50 border-t">
                        <div className="text-xs text-gray-500 truncate border-l-2 border-blue-400 pl-2">
                          Replying to{" "}
                          <span className="font-medium text-gray-700">
                            {replyTo.sender?.name || "message"}
                          </span>
                          : {replyTo.content.body}
                        </div>
                        <button
                          onClick={() => setReplyTo(null)}
                          className="text-gray-400 hover:text-gray-600 shrink-0 ml-2"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {attachmentPreview && (
                      <div className="px-4 py-2 bg-gray-50 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">
                            Attachment Preview
                          </span>
                          <button
                            onClick={() => setAttachmentPreview(null)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="relative group">
                          <img
                            src={attachmentPreview.preview}
                            alt="Preview"
                            className="max-h-32 rounded-lg shadow-sm"
                          />
                        </div>
                      </div>
                    )}
                    <div className="p-4 border-t">
                      <div className="flex w-full min-w-0 items-end gap-2 overflow-hidden rounded-2xl bg-gray-100 p-2">
                        <textarea
                          ref={inputRef}
                          rows={1}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (attachmentPreview) {
                                handleSendAttachment();
                              } else {
                                handleSendMessage();
                              }
                            }
                          }}
                          placeholder="Type your message..."
                          className="w-0 min-w-0 flex-1 resize-none max-h-40 overflow-y-auto border-none bg-transparent px-4 py-1.5 leading-snug focus:outline-none focus:ring-0"
                          disabled={isSending || isUploading}
                        />
                        <div className="flex shrink-0 items-center gap-2 mb-1">
                          <input
                            type="file"
                            id="file-upload"
                            className="hidden"
                            onChange={(e) =>
                              e.target.files && handleFileUpload(e.target.files)
                            }
                            accept="image/*"
                          />
                          <button
                            onClick={() =>
                              document.getElementById("file-upload")?.click()
                            }
                            className="shrink-0 text-gray-500 transition-colors hover:text-gray-700"
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <span className="animate-pulse">⏳</span>
                            ) : (
                              <AttachmentIcon className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              attachmentPreview
                                ? handleSendAttachment()
                                : handleSendMessage()
                            }
                            className="shrink-0 rounded-full bg-blue-500 p-2 text-white transition-colors hover:bg-blue-600 disabled:bg-blue-300"
                            disabled={isSending || isUploading}
                          >
                            {isSending ? (
                              <span className="animate-pulse">⏳</span>
                            ) : (
                              <PaperAirplaneIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
